import { Link, NavLink, useNavigate } from "react-router-dom";
import { LogOut, Menu, Plus, Shield, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";

const nav = [
  ["/", "Sports"],
  ["/live", "Live"],
  ["/racing", "Horse Racing"],
  ["/lotto", "Lotto"],
  ["/fly-high", "Fly High"],
  ["/results", "Results"],
  ["/my-bets", "My Bets"],
];

export const Header = ({ onMenu }) => {
  const { user, setAuthOpen, logout } = useAuth();
  const { currency, setCurrency, currencies, fmt } = useCurrency();
  const navigate = useNavigate();
  return (
    <header className="sticky top-0 z-50 border-b border-[#1E293B] bg-[#0E131F]/90 backdrop-blur-md">
      <div className="flex h-14 items-center gap-3 px-3 sm:px-5">
        <button className="lg:hidden" onClick={onMenu} data-testid="header-menu-button" aria-label="Menu">
          <Menu className="h-5 w-5" />
        </button>
        <Link to="/" data-testid="header-logo" className="font-display text-2xl font-black uppercase italic tracking-tight">
          better<span className="text-[#10B981]">Play</span>
        </Link>
        <nav className="ml-4 hidden items-center gap-0.5 lg:flex">
          {nav.map(([to, label]) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={`header-nav-${label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) =>
                `rounded-md px-2.5 py-1.5 font-display text-sm font-bold uppercase tracking-wide transition-colors ${
                  isActive ? "text-[#10B981]" : "text-slate-300 hover:text-white"
                }`
              }
            >
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-2">
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger data-testid="header-currency-selector" className="h-8 w-[84px] border-[#1E293B] bg-[#121824] text-xs font-semibold">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-[#1E293B] bg-[#121824]">
              {currencies.map((c) => (
                <SelectItem key={c} value={c} data-testid={`currency-option-${c}`}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {user ? (
            <>
              <button
                data-testid="header-balance"
                onClick={() => navigate("/wallet")}
                className="hidden items-center gap-2 rounded-full border border-[#1E293B] bg-[#121824] px-3 py-1.5 text-sm font-semibold sm:flex"
              >
                <Wallet className="h-4 w-4 text-[#10B981]" />
                <span className="font-odds">{fmt(user.balance_usd)}</span>
              </button>
              <Button
                data-testid="header-wallet-deposit-button"
                onClick={() => navigate("/wallet")}
                className="h-8 rounded-full bg-[#10B981] px-3 font-bold text-[#0B0E14] hover:bg-[#059669]"
              >
                <Plus className="h-4 w-4" /> <span className="hidden sm:inline">Deposit</span>
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger data-testid="header-account-menu" className="flex h-8 w-8 items-center justify-center rounded-full bg-[#1A2234] text-sm font-bold uppercase">
                  {user.name?.[0]}
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="border-[#1E293B] bg-[#121824]">
                  <div className="px-2 py-1.5 text-xs text-slate-400">{user.phone}</div>
                  <DropdownMenuItem data-testid="menu-wallet" onClick={() => navigate("/wallet")}>
                    <Wallet className="mr-2 h-4 w-4" /> Wallet & Payouts
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-my-bets" onClick={() => navigate("/my-bets")}>My Bets</DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>
                      <Shield className="mr-2 h-4 w-4" /> Admin
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem data-testid="menu-logout" onClick={logout}>
                    <LogOut className="mr-2 h-4 w-4" /> Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <Button
              data-testid="header-login-button"
              onClick={() => setAuthOpen(true)}
              className="h-8 rounded-full bg-[#10B981] px-4 font-bold text-[#0B0E14] hover:bg-[#059669]"
            >
              Log in / Join
            </Button>
          )}
        </div>
      </div>
    </header>
  );
};
